import { useState, useEffect, useContext, Fragment } from 'react';
import {
    Box,
    List,
    Avatar,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Typography,
    Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttLabelDate } from '../../../sporttia/all';
import sporttiaIcon from '../../../images/sporttia-icon.svg';
import MessageCommentBox from './MessageCommentBox';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

type Message = {
    id: number;
    sender?: {
        fullName: string;
    };
};

type Comment = {
    creator?: {
        name?: string;
        fullName?: string;
    };
    img?: {
        id: number;
    };
    created: string;
    text: string;
};

type CommentsResponse = {
    count: number;
    rows: Comment[];
};

type Props = {
    message: Message;
};

export default function MessageCommentList({
    message,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    // Responder y abrir un privado escriben mensajes: `communication.create`
    const canWrite = can(PERMISSIONS.COMMUNICATION.CREATE);

    const getDisplayName = (name: string | undefined | null): string =>
        !name || name.startsWith('[[') ? t('DeletedUser') : name;
    const [comments, setComments] = useState<CommentsResponse>();
    const [page, _setPage] = useState<number>(1);
    const [rows, setRows] = useState<number>(5);
    const [messageValue, setMessageValue] = useState<Message>({} as Message);

    /**
     * Load comments
     */
    const loadComments = (): void => {
        cxt?.api('GET', `/messages/${messageValue.id}/comments`, {
            params: {
                page,
                rows,
            },
            success: (r: CommentsResponse) => {
                setComments(r);
            },
        });
    };

    /**
     * Send comment
     */
    const sendComment = (text: string): void => {
        cxt?.api('POST', `/messages/${messageValue.id}/comments`, {
            params: {
                text,
            },
            success: (_r: unknown) => {
                loadComments();
            },
        });
    };

    const generatePrivateMessage = (): void => {
        cxt?.api('POST', `/messages/${messageValue.id}/private`, {
            success: (r: { message: Message }) => {
                setMessageValue(r.message);
            },
        });
    };

    /**
     * Message changes
     */
    useEffect(() => {
        if (message && message.id) setMessageValue(message);
    }, [message]);

    /**
     * MessageValue changes
     */
    useEffect(() => {
        if (messageValue && messageValue.id) loadComments();
    }, [messageValue]);

    /**
     * Rows change
     */
    useEffect(() => {
        if (rows && messageValue && messageValue.id) loadComments();
    }, [rows]);

    // -----| Render |-----
    return (
        <Box>
            <List>
                {comments && comments.count > comments.rows.length && (
                    <Button fullWidth onClick={() => setRows(rows + 8)}>
                        {t('ShowMoreResults')}
                    </Button>
                )}

                {comments &&
                    comments.rows.map((comment: any, idxComm: any) => (
                        <Fragment key={idxComm}>
                            <ListItem alignItems="flex-start">
                                <ListItemAvatar>
                                    <Avatar
                                        alt={getDisplayName(
                                            comment.creator?.fullName ||
                                                comment.creator?.name,
                                        )}
                                        src={
                                            comment?.img?.id
                                                ? cxt?.getResource(
                                                      `/images/${comment.img.id}.jpg`,
                                                  )
                                                : sporttiaIcon
                                        }
                                    />
                                </ListItemAvatar>

                                <ListItemText
                                    primary={getDisplayName(
                                        comment.creator?.fullName ||
                                            comment.creator?.name,
                                    )}
                                    secondary={
                                        <Typography
                                            variant="body2"
                                            color="textPrimary"
                                        >
                                            <SttLabelDate
                                                date={comment.created}
                                            />
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            <Box ml={9} mb={2}>
                                <Typography variant="body1">
                                    {comment.text}
                                </Typography>
                            </Box>

                            {idxComm !== comments.rows.length - 1 && (
                                <Divider variant="inset" component="li" />
                            )}
                        </Fragment>
                    ))}
            </List>

            {canWrite && (
                <MessageCommentBox
                    onSendComment={sendComment}
                    onSendPrivateMessage={generatePrivateMessage}
                    senderName={
                        messageValue?.sender
                            ? getDisplayName(messageValue.sender.fullName)
                            : undefined
                    }
                />
            )}
        </Box>
    );
}
